/**
 * TipTap Rich Text Editor component for general use
 */
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Code,
  Undo,
  Redo,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { EDITOR_SURFACE_FILL, EDITOR_SURFACE_PADDING_LG } from '../../styles/editorSurface';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Separator } from '../../../components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group';
import { BlockTextDirection } from './extensions/BlockTextDirection';
import { EditorDirectionToggle } from './EditorDirectionToggle';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  disabled?: boolean;
}

interface EditorToolbarProps {
  editor: Editor;
}

function EditorToolbar({ editor }: EditorToolbarProps) {
  const { t } = useTranslation('common');

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b bg-muted/50">
      {/* Undo/Redo */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('editor.undo')}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('editor.redo')}</TooltipContent>
      </Tooltip>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Text formatting */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-8 w-8', editor.isActive('bold') && 'bg-muted')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('editor.bold')}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-8 w-8', editor.isActive('italic') && 'bg-muted')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('editor.italic')}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-8 w-8', editor.isActive('underline') && 'bg-muted')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('editor.underline')}</TooltipContent>
      </Tooltip>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Lists */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-8 w-8', editor.isActive('bulletList') && 'bg-muted')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('editor.bulletList')}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-8 w-8', editor.isActive('orderedList') && 'bg-muted')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('editor.numberedList')}</TooltipContent>
      </Tooltip>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Alignment */}
      <ToggleGroup type="single" size="sm">
        <ToggleGroupItem
          value="left"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          data-state={editor.isActive({ textAlign: 'left' }) ? 'on' : 'off'}
        >
          <AlignLeft className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="center"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          data-state={editor.isActive({ textAlign: 'center' }) ? 'on' : 'off'}
        >
          <AlignCenter className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="right"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          data-state={editor.isActive({ textAlign: 'right' }) ? 'on' : 'off'}
        >
          <AlignRight className="h-4 w-4" />
        </ToggleGroupItem>
      </ToggleGroup>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Text direction */}
      <EditorDirectionToggle editor={editor} />

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Link */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-8 w-8', editor.isActive('link') && 'bg-muted')}
            onClick={setLink}
          >
            <LinkIcon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('editor.insertLink')}</TooltipContent>
      </Tooltip>

      {/* Code */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-8 w-8', editor.isActive('code') && 'bg-muted')}
            onClick={() => editor.chain().focus().toggleCode().run()}
          >
            <Code className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('editor.code')}</TooltipContent>
      </Tooltip>
    </div>
  );
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = 150,
  disabled = false,
}: RichTextEditorProps) {
  const { t } = useTranslation('common');
  const editor = useEditor({
    extensions: [
      // Disable Link and Underline from StarterKit since we configure them separately
      // TipTap v3 includes these by default, causing "Duplicate extension names" errors
      StarterKit.configure({
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      BlockTextDirection,
      Placeholder.configure({
        placeholder: placeholder ?? t('editor.enterContent'),
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update content when value prop changes externally. emitUpdate:false is
  // essential: syncing the parent's controlled value is NOT a user edit, so it
  // must not fire onChange. Without it, Tiptap v3's setContent (emitUpdate
  // defaults to true) echoes the value straight back through onChange — a
  // feedback loop that, for any auto-save-on-change consumer, persists the
  // editor's empty mount state and clobbers the stored value before it loads.
  // The empty-state guard matches CompactRichTextEditor: an empty editor
  // serializes as '<p dir="auto"></p>', never '', so an empty controlled value
  // would otherwise mismatch forever and re-run setContent on every render,
  // resetting the document and the cursor under the user.
  useEffect(() => {
    if (!editor) return;
    if (value === editor.getHTML()) return;
    if (!value && editor.isEmpty) return;
    editor.commands.setContent(value, { emitUpdate: false });
  }, [value, editor]);

  // Update editable state. The second `false` is `emitUpdate`: Tiptap's
  // setEditable emits an update by default, which would fire onChange with the
  // current HTML even though nothing was edited.
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled, false);
    }
  }, [disabled, editor]);

  if (!editor) {
    return null;
  }

  return (
    <Card className="overflow-hidden">
      {!disabled && <EditorToolbar editor={editor} />}
      {/* minHeight + padding live on the editor surface, never on a wrapper, so
          a click anywhere in the box lands on the contenteditable and focuses
          it — see shared/styles/editorSurface.ts. */}
      {/* bidi-auto-ok: wraps the contenteditable surface. */}
      <div dir="auto" className={cn(disabled && 'opacity-60')}>
        <EditorContent
          editor={editor}
          style={{ minHeight }}
          className={cn(
            'prose prose-sm dark:prose-invert max-w-none',
            'focus:outline-none',
            EDITOR_SURFACE_PADDING_LG,
            EDITOR_SURFACE_FILL,
            '[&_.ProseMirror]:outline-none',
            '[&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]',
            '[&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left',
            '[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground',
            '[&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none',
            '[&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0',
            '[&_.ProseMirror_a]:text-primary',
            '[&_.ProseMirror_a]:underline',
            '[&_.ProseMirror_ul]:ps-6',
            '[&_.ProseMirror_ol]:ps-6',
            '[&_.ProseMirror_code]:bg-muted',
            '[&_.ProseMirror_code]:px-1',
            '[&_.ProseMirror_code]:rounded',
            '[&_.ProseMirror_code]:font-mono'
          )}
        />
      </div>
    </Card>
  );
}
